<template>
    <v-container fluid class="pt-0">
        <create-or-update-user
            :is-open="isCreateOrUpdateDialogVisible"
            :user="selected"
            @close="onCloseCreateOrUpdateDialog"
        />
        <confirm-delete
            :is-open="isConfirmDeleteDialogVisible"
            :selected="selected"
            @confirm="onConfirmDeleteDialog($event)"
            @close="onCloseConfirmDeleteDialog"
        />
        <confirm-restore
            :is-open="isConfirmRestoreDialogVisible"
            :selected="selected"
            @confirm="onConfirmRestoreDialog($event)"
            @close="onCloseConfirmRestoreDialog"
        />
        <entity-history
            :isVisible="isShowHistoryDialogVisible"
            :histories="histories"
            @close="onCloseShowHistoryDialog"
        >
            <template #title>
                User History
            </template>
        </entity-history>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <h1>{{ $t('employeesView.title') }}</h1>
                            </v-col>
                            <v-col cols="12" sm="6" md="9">
                                <v-btn
                                    depressed
                                    medium
                                    class="mt-2"
                                    @click="onOpenCreateOrUpdateDialog(false)"
                                >
                                    {{ $t('employeesView.newEmployee') }}
                                </v-btn>
                            </v-col>
                            <import-from-csv
                                v-if="isAdmin"
                                @upload="onCsvUpload"
                            />
                        </v-row>
                        <v-row>
                            <v-col cols="12">
                                <v-tabs v-model="currentRoleTab">
                                    <v-tab
                                        v-for="tabName in tabNames"
                                        :key="tabName"
                                    >
                                        {{ tabName }}
                                    </v-tab>
                                </v-tabs>
                            </v-col>
                        </v-row>
                        <v-tabs-items v-model="currentRoleTab">
                            <v-tab-item>
                                <employee-user-list
                                    :users="users"
                                    :count="userCount"
                                    @findUsers="findUsers"
                                    @edit="onOpenCreateOrUpdateDialog"
                                    @delete="onDeleteItem"
                                    @showHistory="onShowHistory"
                                    @restore="onRestoreItem"
                                />
                            </v-tab-item>

                            <v-tab-item>
                                <employee-admin-list
                                    :users="admins"
                                    :count="adminCount"
                                    @findUsers="findUsers"
                                    @edit="onOpenCreateOrUpdateDialog"
                                    @delete="onDeleteItem"
                                    @showHistory="onShowHistory"
                                    @restore="onRestoreItem"
                                />
                            </v-tab-item>

                            <v-tab-item>
                                <employee-deleted-list
                                    :users="deletedUsers"
                                    :count="deletedCount"
                                    @findUsers="findUsers"
                                    @edit="onOpenCreateOrUpdateDialog"
                                    @delete="onDeleteItem"
                                    @showHistory="onShowHistory"
                                    @restore="onRestoreItem"
                                />
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes } from 'http-status-codes';

export default {
    name: 'employees',
    components: {
        EmployeeUserList: () => import('@/components/EmployeeUserList'),
        EmployeeAdminList: () => import('@/components/EmployeeAdminList'),
        EmployeeDeletedList: () => import('@/components/EmployeeDeletedList'),
        CreateOrUpdateUser: () => import('@/components/CreateOrUpdateUser'),
        ConfirmDelete: () => import('@/components/ConfirmDelete'),
        ConfirmRestore: () => import('@/components/ConfirmRestore'),
        EntityHistory: () => import('@/components/EntityHistory'),
        ImportFromCsv: () => import('@/components/ImportFromCsv')
    },
    data() {
        return {
            isCreateOrUpdateDialogVisible: false,
            isConfirmDeleteDialogVisible: false,
            isConfirmRestoreDialogVisible: false,
            selected: null,
            currentRoleTab: null,
            currentTab: null,
            tabNames: {
                user: this.$t('employeesView.tabNames.activeEmployees'),
                admin: this.$t('employeesView.tabNames.administrators'),
                deleted: this.$t('employeesView.tabNames.deletedEmployees')
            },
            isShowHistoryDialogVisible: false,
            histories: []
        };
    },
    computed: {
        ...mapGetters({
            users: 'users/getUsers',
            admins: 'users/getAdmins',
            deletedUsers: 'users/getDeletedUsers',
            userCount: 'users/getUserCount',
            adminCount: 'users/getAdminCount',
            deletedCount: 'users/getDeletedCount',
            isAdmin: 'auth/isAdmin'
        })
    },
    watch: {
        params: {
            async handler(params) {
                await this.findUsers(params);
            },
            deep: true
        },

        adminParams: {
            async handler(params) {
                await this.findUsers(params);
            },
            deep: true
        },

        deletedParams: {
            async handler(params) {
                await this.findUsers(params);
            },
            deep: true
        }
    },
    methods: {
        ...mapActions({
            fetchUsers: 'users/fetch',
            deleteUser: 'users/delete',
            restoreUser: 'users/restore',
            fetchHistoriesByUserId: 'users/fetchHistoriesByEntityId',
            importCsv: 'users/importCsv'
        }),

        async findUsers(params) {
            try {
                await this.fetchUsers(params);
            } catch (err) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('employeesView.title'),
                    text: this.$t('formCommonErrors.serverError')
                });

                console.error(err);
            }
        },

        onDeleteItem(item = {}) {
            this.selected = item;
            this.isConfirmDeleteDialogVisible = true;
        },

        onRestoreItem(item = {}) {
            this.selected = item;
            this.isConfirmRestoreDialogVisible = true;
        },

        async onConfirmRestoreDialog(item) {
            try {
                await this.restoreUser({ userId: item.id, roles: item.roles });

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('employeesView.employeeTitle'),
                    text: this.$t('employeesView.restoreSuccess')
                });
            } catch (error) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('employeesView.employeeTitle'),
                    text: this.$t('employeesView.deleteError')
                });

                console.error(error);
            }

            this.onCloseConfirmRestoreDialog();
        },

        async onConfirmDeleteDialog(item) {
            try {
                await this.deleteUser({ userId: item.id, roles: item.roles });

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('employeesView.employeeTitle'),
                    text: this.$t('employeesView.deleteSuccess')
                });
            } catch (error) {
                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('employeesView.employeeTitle'),
                    text: this.$t('employeesView.deleteError')
                });

                console.error(error);
            }

            this.onCloseConfirmDeleteDialog();
        },

        onCloseConfirmRestoreDialog() {
            this.selected = null;
            this.isConfirmRestoreDialogVisible = false;
        },

        onCloseConfirmDeleteDialog() {
            this.selected = null;
            this.isConfirmDeleteDialogVisible = false;
        },

        onOpenCreateOrUpdateDialog(item = {}) {
            this.isCreateOrUpdateDialogVisible = true;

            if (item.id) {
                this.selected = item;
            }
        },

        onCloseCreateOrUpdateDialog() {
            this.selected = null;
            this.isCreateOrUpdateDialogVisible = false;
        },

        async onShowHistory({ id }) {
            try {
                this.histories = await this.fetchHistoriesByUserId(id);
            } catch (err) {
                const { response } = err;

                if (response && response.status === 404) {
                    return;
                }

                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('formCommonErrors.commonTitle'),
                    text: this.$t('formCommonErrors.serverError')
                });

                console.error(err);
            } finally {
                this.isShowHistoryDialogVisible = true;
            }
        },

        onCloseShowHistoryDialog() {
            this.histories = [];
            this.isShowHistoryDialogVisible = false;
        },

        async onCsvUpload(csvFile) {
            try {
                await this.importCsv(csvFile);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.$t('importFromCsv.csvUploadForm.uploadTitle'),
                    text: this.$t('importFromCsv.csvUploadForm.uploadSuccess')
                });
            } catch (error) {
                if (
                    !error.response ||
                    error.response.status !== StatusCodes.BAD_REQUEST
                ) {
                    console.error(error);

                    return this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.$t(
                            'importFromCsv.csvUploadForm.uploadTitle'
                        ),
                        text: this.$t('formCommonErrors.serverError')
                    });
                }

                this.$notify({
                    group: 'notify',
                    type: 'error',
                    title: this.$t('importFromCsv.csvUploadForm.uploadTitle'),
                    text: this.$t('importFromCsv.csvUploadForm.incorrectCsv')
                });
            }
        }
    }
};
</script>
